import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TRABADJU_INFO as T } from '@/utils/businessInfo';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PoliticaDevolucoes() {
  const { t } = useTranslation();
  const today = new Date().toLocaleDateString('pt-PT');
  return (
    <div className="min-h-screen bg-gray-50 py-10" data-testid="politica-devolucoes-page">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/">
          <Button variant="ghost" className="mb-4" data-testid="back-to-home-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('legal.back')}
          </Button>
        </Link>
        <article className="bg-white rounded-xl shadow-sm p-8 prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold mb-2">{t('legal.refundTitle')}</h1>
          <p className="text-sm text-gray-500 mb-2">{t('legal.lastUpdated', { date: today })}</p>
          <p className="text-xs text-gray-400 mb-6 not-italic">{t('legal.ptOnlyNotice')}</p>

          <h2>1. Natureza do serviço</h2>
          <p>
            O <strong>{T.brand}</strong> ({T.website}) é uma plataforma digital de classificados
            de emprego. Não vende produtos físicos: comercializa <strong>créditos digitais</strong>{' '}
            que permitem ao empregador publicar anúncios de vagas de emprego.
          </p>

          <h2>2. Entrega dos créditos</h2>
          <ul>
            <li>
              <strong>Imediata e automática.</strong> Assim que o pagamento é confirmado pela
              rede vinti4/SISP ou pelo processador internacional (Stripe), os créditos são
              creditados na conta do empregador de forma automática — normalmente em menos de
              30 segundos.
            </li>
            <li>
              <strong>Confirmação por email.</strong> É enviado um email para{' '}
              <a href={`mailto:${T.email_support}`}>{T.email_support}</a> e para o email
              registado do empregador com o número da factura e o número de créditos adquiridos.
            </li>
            <li>
              <strong>Sem custos de envio.</strong> Não há portes nem prazos de entrega:
              o serviço é integralmente digital.
            </li>
          </ul>

          <h2>3. Reembolso e devolução</h2>
          <p>
            <strong>Prazo:</strong> o empregador pode solicitar o reembolso integral da compra
            até <strong>24 horas</strong> após o pagamento, desde que{' '}
            <strong>nenhum crédito adquirido tenha sido utilizado</strong> (ou seja, nenhum
            anúncio tenha sido publicado ou destacado com esses créditos).
          </p>
          <p>
            Após o uso de qualquer crédito, a compra deixa de ser reembolsável, uma vez que
            o serviço passa a estar prestado (o anúncio fica visível na plataforma).
          </p>
          <p>
            Este prazo respeita o disposto no Código do Consumidor de Cabo Verde e é aplicado
            de forma uniforme, independentemente do meio de pagamento utilizado (vinti4, Visa,
            Mastercard, American Express, Stripe).
          </p>

          <h2>4. Como pedir um reembolso</h2>
          <ol>
            <li>
              Enviar um email para{' '}
              <a href={`mailto:${T.email_support}`}>{T.email_support}</a> com o assunto{' '}
              <em>&quot;Pedido de reembolso — [Nº da factura]&quot;</em>.
            </li>
            <li>
              Indicar o motivo do pedido (opcional, mas ajuda-nos a melhorar).
            </li>
            <li>
              A nossa equipa responde no prazo máximo de <strong>3 dias úteis</strong> a
              confirmar a devolução.
            </li>
            <li>
              O reembolso é efectuado pelo mesmo meio de pagamento usado na compra
              (cartão vinti4 → mesmo cartão vinti4; Stripe → mesmo cartão internacional).
              O valor pode demorar até <strong>10 dias úteis</strong> a aparecer no extracto,
              dependendo do banco emissor.
            </li>
          </ol>

          <h2>5. Falhas técnicas ou pagamentos duplicados</h2>
          <p>
            Se um pagamento for cobrado mais do que uma vez por falha técnica, ou se os
            créditos não forem entregues após o pagamento ser confirmado, o empregador tem
            direito a <strong>reembolso integral e imediato</strong>, sem qualquer prazo
            limite. Basta contactar{' '}
            <a href={`mailto:${T.email_support}`}>{T.email_support}</a> anexando a prova
            de pagamento.
          </p>

          <h2>6. Reclamações e apoio ao consumidor</h2>
          <p>Contactos oficiais de apoio:</p>
          <ul>
            <li>
              <strong>Email:</strong>{' '}
              <a href={`mailto:${T.email_support}`}>{T.email_support}</a>
            </li>
            <li>
              <strong>Comerciante:</strong> {T.legal_name}
            </li>
            <li>
              <strong>NIF:</strong> {T.nif}
            </li>
            <li>
              <strong>Morada:</strong> {T.address}
            </li>
            <li>
              <strong>Website:</strong>{' '}
              <a href={T.website} target="_blank" rel="noopener noreferrer">
                {T.website}
              </a>
            </li>
          </ul>

          <h2>7. Legislação aplicável</h2>
          <p>
            Esta política rege-se pela lei da República de Cabo Verde. Qualquer litígio
            emergente será submetido à jurisdição dos tribunais da cidade da Praia,
            com renúncia expressa a qualquer outro foro.
          </p>

          <p className="mt-8">
            <Link to="/termos" className="text-blue-600 hover:underline">
              {t('legal.seeTermsLink')}
            </Link>
          </p>
        </article>
      </div>
    </div>
  );
}
